import Home from './components/Home.js';
import NavMenu from './components/NavMenu.js';
import Footer from './components/Footer.js';
import Consent from './components/support/Consent.js';
import NotFound from './components/support/NotFound.js';

import Areas from './components/support/Areas.js';
import ScrollToTop from './components/support/ScrollToTop.js';
import useAuth from './components/auth/useAuth.js';
import { ProvideAuth } from './components/auth/useAuth.js';
import PrivateRoute from './components/auth/PrivateRoute.js';
import { ProvideSettings } from './components/settings/useSettings.js';
import { ProvideAnalytics } from './components/support/Analytics.js';

import Chatbot from './components/chatbot/Chatbot.js';

import Pages from './components/pages/Pages.js';
import Glossary from './components/glossary/Glossary.js';

import theme from './components/theme';
import jss from './components/jss';
import { getClassGen } from './components/jss';

import React, { useState, useRef, lazy, Suspense, useEffect } from 'react';
import axios from 'axios';
import { Route, Switch, useLocation, Redirect } from 'react-router';
import { Link } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import { StylesProvider } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DarkModeProvider } from './components/support/DarkModeContext';

 import './custom.css';

const Login = lazy(() => import('./components/auth/Login.js'));
const Users = lazy(() => import('./components/users/Users.js'));
const Tags = lazy(() => import('./components/tags/Tags.js'));
const Settings = lazy(() => import('./components/settings/Settings.js'));
const FileManager = lazy(() => import('./components/files/FileManager.js'));
const Logs = lazy(() => import('./components/logs/Logs.js'));
const NavItems = lazy(() => import('./components/navitems/NavItems.js'));
const Trash = lazy(() => import('./components/pages/Trash.js'));
const PageAttendances = lazy(() => import('./components/pageAttendances/PageAttendances.js'));
const SSORedir = lazy(() => import('./components/auth/SSOLanding.js'));

const App = (props) => {
    let auth = useAuth();
    const mainRef = useRef();

    const [login, setLogin] = useState(false);

    const [ax, setAx] = React.useState(false);
    if (!ax) {
        axios.interceptors.response.use(function (response) {
            return response;
        }, function (error) {
            var err = error;
            if ('response' in err) {
                err = error.response;
            }
            if (err) {
                if (err.status === 401) {
                    setLogin(true);
                    auth.signout(true);
                } else if (err.status === 403) {
                    console.log("invalid permissions", err.message);
                }
            } else if (error.request) {
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
            return Promise.reject(error);
        });
        setAx(true);
    }

    const onLogin = (success) => {
        if (success) {

        } else {

        }
        setLogin(false);
    };

    const unauthDev = () => {
        if (process.env.REACT_APP_ENV !== "Development")
            return false;
        if (auth?.user?.id)
            return false;
        if (window.localStorage.getItem("lthub_has_loggedin") === "1")
            return false;
        return true;
    };
    return (
        <ProvideAuth>
            <ProvideSettings>
                <ProvideAnalytics>
                    <DarkModeProvider>           
                    <ThemeProvider theme={theme}>
                        <StylesProvider jss={jss} generateClassName={getClassGen(props.prerendering)}>
                            <CssBaseline />
                            <Container maxWidth="lg">                            
                                <ScrollToTop />
                                <Link className="skip-link" to={{ hash: "#main-content" }} tabIndex="0" onClick={() => mainRef.current.scrollIntoView({ behavior: 'smooth' })}>Skip to Content</Link>
                                <NavMenu />
                                <main id="main-content" ref={mainRef}>
                                    <Switch>
                                        <Route exact path='/' component={Home}>{typeof window != "undefined" ? <Home /> : null}</Route>
                                        {!unauthDev() ? <React.Fragment>
                                            <Route exact path='/home' component={Home} />

                                            {Areas.map((value, index) => {
                                                return (<Route key={index} path={'/' + value.url} component={Pages} />);
                                            })}
                                            <Route exact path='/glossary' component={Glossary} />

                                            <Route exact path='/login'><Suspense fallback={<CircularProgress />}><Login /></Suspense></Route>
                                            <PrivateRoute exact path='/nav' roles={["admin"]}><Suspense fallback={<CircularProgress />}><NavItems /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/users' roles={["admin"]}><Suspense fallback={<CircularProgress />}><Users /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/tags' roles={["admin"]}><Suspense fallback={<CircularProgress />}><Tags /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/settings' roles={["admin"]}><Suspense fallback={<CircularProgress />}><Settings /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/filemanager' roles={["article.contributor", "collection.contributor", "showcase.contributor", "page.contributor", "unlisted.contributor"]}><Suspense fallback={<CircularProgress />}><FileManager /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/logs' roles={["data/reporting"]}><Suspense fallback={<CircularProgress />}><Logs /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/trash' roles={["admin"]}><Suspense fallback={<CircularProgress />}><Trash /></Suspense></PrivateRoute>
                                            <PrivateRoute exact path='/pageattendances' roles={["admin"]}><Suspense fallback={<CircularProgress />}><PageAttendances /></Suspense></PrivateRoute>
                                            <Route exact path='/sso'><Suspense fallback={<CircularProgress />}><SSORedir /></Suspense></Route>
                                            </React.Fragment>
                                         : null}
                                        <Route path="*">
                                            <NoMatch />
                                        </Route>
                                    </Switch>
                                    <Suspense fallback={<CircularProgress />}>
                                        <Login embed={true} open={login} onLogin={onLogin} message="Invalid authorisation, please login and try again." canCancel={true} />
                                    </Suspense>
                                    <Consent />
                                </main>
                                <Chatbot />
                                <Footer />
                            </Container>
                        </StylesProvider>
                        </ThemeProvider>
                    </DarkModeProvider>
                </ProvideAnalytics>
            </ProvideSettings>
        </ProvideAuth>
    );
};

function NoMatch() {
    let location = useLocation();
    console.log(window.location.pathname);
    if (window.location.pathname.indexOf('.css') >= 0) {
        return <div />; // must return at least an empty div
    } else {
        return (
            <NotFound />
            //<div>
            //    <h3>
            //        No match for <code>{location.pathname}</code>
            //    </h3>
            //</div>
        );
    }
}

export default App;
